import locationsStep from './steps/locations.js';
import subscriptionsStep from './steps/subscription.js';
import servicesStep from './steps/services.js';
import employeesStep from './steps/employees.js';
import calendarStep from './steps/calendar.js';
import authStep from './steps/auth.js';
import confirmStep from './steps/confirm.js';
import thanksStep from './steps/thanks.js';
import {Debouncer} from './utils/debounce.js';
import {t} from './i18n.js';
import {renderHeader, renderBottomBar, svgImages} from './utils/templates.js';
import {initCategoryScrollSync, destroyCategoryScrollSync} from './utils/categoryScroller.js';
import {shout} from "./utils/shout.js";
import {isNonNullObject} from "./utils/helpers.js";
import {seedAuthState, beginReauth} from './auth/authState.js';

const stepsMap = {
    locations:    () => new locationsStep(),
    subscription: () => new subscriptionsStep(),
    services:     () => new servicesStep(),
    employees:    () => new employeesStep(),
    calendar:     () => new calendarStep(),
    auth:         () => new authStep(),
    confirm:      () => new confirmStep(),
    thanks:       () => new thanksStep()
};

let isWidget = false;
let isClass = false;
let useLocationSelector = false;

const LIMIT_WARNING_ID = 'limitWarning'; // used for debounce


// function getEffectiveStepOrder1(bwData, bwState) {
//     const steps = [];
//
//     const hasOneEmployee = bwData.employees?.length === 1;
//     const hasOneService = bwData.services?.length === 1;
//
//     const canSelectEmployee = bwData.config.canSelectEmployee !== false;
//     const canSelectService = bwData.config.canSelectService !== false;
//
//     // Attach flags to bwState for use inside step modules
//     bwState.canSelectEmployee = canSelectEmployee && !hasOneEmployee;
//     bwState.canSelectService = canSelectService && !hasOneService;
//
//     // Preselect single employee/service if needed
//     if (!canSelectEmployee || hasOneEmployee) {
//         const emp = bwData.employees?.[0];
//         if (emp) bwState.selectedEmployeeId = emp.id;
//     }
//
//     if (!canSelectService || hasOneService) {
//         const svc = bwData.services?.[0];
//         if (svc) bwState.selectedServices = [svc];
//     }
//
//     // Determine order
//     const baseOrder = getStepOrder(bwData.hasEmployeesFirst);
//
//     for (const step of baseOrder) {
//         if (step === 'employees' && !bwState.canSelectEmployee) continue;
//         if (step === 'services' && !bwState.canSelectService) continue;
//         steps.push(step);
//     }
//
//     return steps;
// }

// function getEffectiveStepOrder2(bwData, bwState) {
//     const steps = [];
//
//     const hasOneEmployee = bwData.employees?.length === 1;
//     const hasOneService = bwData.services?.length === 1;
//
//     const canSelectEmployee = bwData.config.canSelectEmployee !== false;
//     const canSelectService = bwData.config.canSelectService !== false;
//
//     const hasSelectedEmployee = !!bwState.selectedEmployee;
//     const hasSelectedServices = bwState.selectedServices?.length > 0;
//
//     // Attach flags for step modules
//     bwState.canSelectEmployee = canSelectEmployee && !hasOneEmployee && !hasSelectedEmployee;
//     bwState.canSelectService = canSelectService && !hasOneService && !hasSelectedServices;
//
//     // Auto-select employee/service from data if needed
//     if (!bwState.selectedEmployee && (!canSelectEmployee || hasOneEmployee)) {
//         bwState.selectedEmployee = bwData.employees?.[0]?.id;
//     }
//
//     if ((!bwState.selectedServices || bwState.selectedServices.length === 0) && (!canSelectService || hasOneService)) {
//         const svc = bwData.services?.[0];
//         if (svc) bwState.selectedServices = [svc.id];
//     }
//
//     const baseOrder = getStepOrder(bwData.hasEmployeesFirst || hasSelectedEmployee);
//
//     for (const step of baseOrder) {
//         if (step === 'employees' && !bwState.canSelectEmployee) continue;
//         if (step === 'services' && !bwState.canSelectService) continue;
//         steps.push(step);
//     }
//
//     return steps;
// }

// function getStepOrder(hasEmployeesFirst, auth) {
//     let steps = hasEmployeesFirst
//         ? ['employees', 'services', 'calendar']
//         : ['services', 'employees', 'calendar'];
//     if (!auth.isChecked) {
//         steps.push('auth');
//     }
//     if (!auth.skipConfirmation) {
//         steps.push('confirm');
//     }
//     return steps;
// }

function getStepOrderContext(config, bwState) {
    const hasSelectedEmployee = !!bwState.selectedEmployee;
    const hasSelectedService = bwState.selectedServices?.length > 0;

    let employeesFirst;

    if (isNonNullObject(bwState.selectedSubscription)) {
        employeesFirst = false;
    } else if (hasSelectedEmployee && !hasSelectedService) {
        employeesFirst = true;
    } else if (hasSelectedService && !hasSelectedEmployee) {
        employeesFirst = false;
    } else {
        employeesFirst = config.employees_first === 1;
    }

    return {
        employeesFirst,
        canSelectEmployee: config.canSelectEmployee !== false,
        canSelectService: config.canSelectService !== false,
        skipConfirmation: config.skipConfirmation === true
    };
}

/**
 * @param {BookingWidgetData} bwData
 * @param {*} bwState
 */
function getVisibleSteps(bwData, bwState) {
    const context = getStepOrderContext(bwData.config, bwState);
    const steps = [];

    const hasOneLocation = bwData.locations?.length === 1;
    const hasOneEmployee = bwData.employees?.length === 1;
    const hasOneService = bwData.services?.length === 1;

    bwState.canSelectEmployee = context.canSelectEmployee && !hasOneEmployee;
    bwState.canSelectService = context.canSelectService && !hasOneService;

    // Auto-select locations
    if (!bwState.selectedLocation && hasOneLocation) {
        useLocationSelector = false;
        bwState.selectedLocation = bwData.locations?.[0]?.id;
    }

    // Auto-select employee
    if (!bwState.selectedEmployee && (!bwState.canSelectEmployee || hasOneEmployee)) {
        bwState.selectedEmployee = bwData.employees?.[0]?.id;
    }

    // Auto-select services
    if ((!bwState.selectedServices || bwState.selectedServices.length === 0) &&
        (!bwState.canSelectService || hasOneService)) {
        const svc = bwData.services?.[0];
        if (svc) bwState.selectedServices = [svc.id];
    }

    // Determine step order
    let order;
    if (isClass) {
        order = [];
    } else {
        if (context.employeesFirst) {
            order = ['employees', 'services', 'calendar'];
        } else {
            order = ['services', 'employees', 'calendar'];
        }
    }

    if(bwData.hasSubscriptions && bwData.usableSubscriptions?.length > 0) {
        order.unshift('subscription');
        if(isNonNullObject(bwState.selectedSubscription)) {
            // const type = bwState.selectedSubscription.type || 'single';
            // if(type === 'group') {
            //     bwState.canSelectService = false
            // }
            const meta = bwState.subscriptionMeta;

            if (meta && meta.requiresServicesStep === false) {
                bwState.canSelectService = false;
            }
        }
    }

    // very first
    if(useLocationSelector) {
        order.unshift('locations');
    }

    if (!bwData.auth?.isChecked) {
        order.push('auth');
    }

    if (!context.skipConfirmation) {
        order.push('confirm');
    }

    for (const step of order) {
        if (step === 'employees' && !bwState.canSelectEmployee) continue;
        if (step === 'services' && !bwState.canSelectService) continue;
        steps.push(step);
    }

    // Expose context if needed by UI or per-step logic
    bwState.stepContext = context;

    return steps;
}

function getInitialStepIndex(steps, bwState, bwData) {
    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        switch (step) {
            case 'locations':
                if (!bwState.selectedLocation) return i;
                break;
            case 'subscription':
                if (!bwState.selectedSubscription) return i; // if !sub or !-1 (!skip)
                break;
            case 'employees':
                if (!bwState.selectedEmployee) return i;
                break;
            case 'services':
                if (!bwState.selectedServices || bwState.selectedServices.length === 0) return i;
                break;
            case 'calendar':
                if (!bwState.date || !bwState.startAt) return i;
                break;
            // future-proof
            case 'auth':
                if (!shouldSkipAuthStep(bwData.auth)) return i;
                break;
            default:
                break;
        }
    }

    // All steps completed, return the last one (e.g., confirm)
    return steps.length - 1;
}

function getInitialStepWithoutSubscriptions(steps, bwState, bwData) {
    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        switch (step) {
            case 'locations':
                if (!bwState.selectedLocation) return i;
                break;
            case 'employees':
                if (!bwState.selectedEmployee) return i;
                break;
            case 'services':
                if (!bwState.selectedServices || bwState.selectedServices.length === 0) return i;
                break;
            case 'calendar':
                if (!bwState.date || !bwState.startAt) return i;
                break;
            // future-proof
            case 'auth':
                if (!shouldSkipAuthStep(bwData.auth)) return i;
                break;
            default:
                break;
        }
    }

    // All steps completed, return the last one (e.g., confirm)
    return steps.length - 1;
}

function setSubscriptionToState(state, subscription) {
    // Reset dependent selections
    state.selectedSubscription = subscription;

    // Meta helper object
    state.subscriptionMeta = {
        requiresServicesStep: true
    };

    // If skipped or invalid (-1)
    if (!isNonNullObject(subscription)) {
        return;
    }

    const type = subscription.type || 'single';
    const serviceIds = subscription.service_ids || [];

    // Reset if previously set
    if(isNonNullObject(state.selectedSubscription)) {
        state.selectedServices = [];
        state.selectedEmployee = null;
    }

    // GROUP subscription
    if (type === 'group') {
        state.selectedServices = [...serviceIds];
        state.subscriptionMeta.requiresServicesStep = false;
        return;
    }

    // SINGLE subscription
    if (type === 'single') {

        if (serviceIds.length === 1) {
            // Auto-select only service
            state.selectedServices = [serviceIds[0]];
            state.subscriptionMeta.requiresServicesStep = false;
        } else {
            // Multiple services → user must choose
            state.selectedServices = [];
            state.subscriptionMeta.requiresServicesStep = true;
        }
    }
}


function shouldSkipAuthStep(auth) {
    return auth?.isAuth && auth?.isChecked;
}

function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36); // Convert to base-36 for shorter hash
}

function getInstanceId($container) {
    return $container.data('instanceId') || $container[0].dataset.instanceId || simpleHash($container[0].outerHTML);
}

// ALL storage access is guarded: browsers with site data blocked (Chrome
// "block cookies" settings, some webviews) throw SecurityError on the mere
// ACCESS of sessionStorage/localStorage. Unguarded, that killed the whole
// widget at init (field telemetry: clinicaarini, Android Chrome). Without
// storage the widget still works — state just doesn't survive reloads.
function persistState(instanceId, state) {
    if (typeof(updateDebugOutput) === 'function') {
        updateDebugOutput();
    } // Safely call without knowing if it's defined
    try {
        sessionStorage.setItem(`bookingWidgetState:${instanceId}`, JSON.stringify(state));
    } catch (e) { /* storage blocked — continue without persistence */ }
}

function restoreState(instanceId) {
    try {
        const data = sessionStorage.getItem(`bookingWidgetState:${instanceId}`);
        clearState(instanceId); // restore it only once on social logins.
        return data ? JSON.parse(data) : {};
    } catch (e) {
        return {};
    }
}

function clearState(instanceId) {
    try {
        sessionStorage.removeItem(`bookingWidgetState:${instanceId}`);
    } catch (e) { /* storage blocked */ }
}

export async function initApp({$, rootContainer, api}) {
    const dataAttrs = rootContainer.data();
    const instanceId = getInstanceId(rootContainer);
    rootContainer.attr('iid', instanceId);
    const body = $('body');
    let subscriptionLogin = false;

    // Data: static, config-based
    /** @type {BookingWidgetData} */
    let bwData = window.bwData || null;
    const apiGetAsync = (url, options = {}) => {
        return new Promise((resolve, reject) => {
            api.get(url, {
                ...options,
                success: resolve,
                error: reject
            });
        });
    };

    if (!bwData) {
        if (dataAttrs.hasOwnProperty('accountToken')) {
            try {
                rootContainer.addClass('bwEmbedded');

                const accountToken = dataAttrs.accountToken;
                const sessionToken = getSessionToken();
                const params = new URLSearchParams({
                    embedded: 'true'
                });

                api.setToken(accountToken);

                if (sessionToken) {
                    params.set('session_token', sessionToken);
                }

                if (dataAttrs.lang) {
                    params.set('lng', dataAttrs.lang);
                }

                const scriptTag = document.getElementById('booking-widget');
                const scriptUrl = new URL(scriptTag.src);
                const baseUrl = scriptUrl.origin;

                const result = await $.ajax({
                    url: `${baseUrl}/api/booking/widget?${params.toString()}`,
                    method: 'GET',
                    headers: {'Authorization': `Bearer ${accountToken}`},
                });

                isWidget = true;

                bwData = {
                    ...result.bwData,
                    config: result.bwData.config || {},
                    services: result.bwData.services,
                    categories: result.bwData.categories,
                    employees: result.bwData.employees,
                    locations: result.bwData.locations,
                    csrf: result.bwData.csrf,
                    apiRoutes: result.bwData.apiRoutes,
                    lang: result.bwData.lang,

                    usableSubscriptions: [],
                    // hasEmployeesFirst: result.bwData.hasEmployeesFirst,
                };
                api.setCSRF(result.bwData.csrf);
                saveSessionToken(result.bwData.sessionToken);
            } catch (err) {
                console.error("Failed to load bwData:", err);
                return;
            }
        }
        else {
            alert('Missing account token');
        }
    }

    // Step 1: Default state
    const defaultState = {
        selectedLocation: null,
        selectedServices: [],
        selectedEmployee: null,
        selectedSubscription: null,
    };

    // Step 2: Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    // function getArrayParam(paramName) {
    //     return [...urlParams.entries()]
    //         .filter(([key]) => key === `${paramName}[]`)
    //         .map(([, value]) => Number(value));
    // }

    const urlState = {};
    if (urlParams.get('locationId')) {
        urlState.selectedLocation = Number(urlParams.get('locationId'));
    }
    if (urlParams.get('serviceIds')) {
        const servicesFromUrl = urlParams.get('serviceIds').split(',');
        if (servicesFromUrl.length) {
            urlState.selectedServices = servicesFromUrl.map(id => Number(id));
        }
    }
    if (urlParams.get('employeeId')) {
        urlState.selectedEmployee = Number(urlParams.get('employeeId'));
    }

    // backward compatible
    if (urlParams.get('servicesIds')) {
        const servicesFromUrl = urlParams.get('servicesIds').split(',');
        if (servicesFromUrl.length) {
            urlState.selectedServices = servicesFromUrl.map(id => Number(id));
        }
    }
    if (urlParams.get('employId')) {
        urlState.selectedEmployee = Number(urlParams.get('employId'));
    }

    // reschedule
    if ( typeof urlParams.get("pending") !== "undefined" && urlParams.get("pending")) {
        urlState.pending = urlParams.get("pending"); // pending=true or pending=false - ?maybe string
    }
    if (typeof urlParams.get("appointmentId") !== "undefined" && urlParams.get("appointmentId")) {
        urlState.appointmentId = Number(urlParams.get("appointmentId"));
    }

    // subscriptions
    if (urlParams.get('subscriptionId')) {
        const subId = Number(urlParams.get('subscriptionId'));
        if(bwData.hasSubscriptions) {
            if(subId > 0) {
                // const sub = bwData.subscriptions.find(sub => sub.id === subId);
                const sub = bwData.auth.user.all_relevant_subscription_purchases.find(sub => sub.id === subId);
                if (sub) {
                    // urlState.selectedServices = sub.services.map(s => s.id);
                    // urlState.selectedServices = sub.service_ids;
                    // urlState.selectedSubscription = sub;
                    setSubscriptionToState(urlState, sub);
                } else {
                    shout("User subscription not found");
                }
            } else {
                // urlState.selectedSubscription = -1; // number
                setSubscriptionToState(urlState, -1);
            }
        } else {
            shout("Parameter `subscriptionId` found on account without subscriptions.");
        }
    }

// class
    if (urlParams.get('classId')) {
        urlState.selectedClass = urlParams.get('classId');

        try {
            // 🔥 Wait for API response
            const data = await apiGetAsync(
                bwData.apiRoutes.getClassInfo,
                { data: { classId: urlState.selectedClass } }
            );

            if (data && data.hasOwnProperty('employee')) {
                if(data.class_status == 'open') {
                    urlState.selectedEmployee = data.employee;
                    urlState.selectedLocation = data.location;
                    urlState.selectedServices = data.services;
                    urlState.date = data.date;
                    urlState.startAt = data.startAt;
                    urlState.endAt = data.endAt;
                } else {
                    // TODO: Show class full error and links back.
                    alert("Class is " + data.class_status + " // TODO");
                }
            } else {
                shout("An error occurred while getting class info. " + (data.error || ""));
            }
        } catch (err) {
            shout("An error occurred while loading class info.");
            console.error(err);
        }

        urlState.isClass = isClass = true;
    }

    // Step 3: Load saved state (highest priority).
    // Client sessionStorage covers the same-origin flow. bwData.restoreState is the
    // server-provided fallback for the embedded popup-blocked case, where the widget
    // reloads on our origin after a top-level social-login redirect and the client
    // store (saved on the customer's origin) is unreachable. Client wins when both exist.
    const savedState = {
        ...(bwData && bwData.restoreState ? bwData.restoreState : {}),
        ...restoreState(instanceId),
    };

    // Step 4: Compose the final state (merged in correct priority order)
    let bwState = {
        ...defaultState,

        // from data attributes
        selectedLocation: dataAttrs.selectedLocation || null,
        selectedServices: dataAttrs.selectedServices || [], // attribute as json array is converted by jQuery.data()
        selectedEmployee: dataAttrs.selectedEmployee || null,
        // selectedSubscription <- not applicable, user specific

        // from URL (only if values are not null/empty)
        ...urlState,

        // saved state always overrides everything
        ...savedState,
    };

    // bwState.iid = instanceId;
    // window.bwState = bwState;



    if (!bwData) {
        console.error("Missing bwData or accountToken");
        return;
    }

    if(bwData.locationsCount === 0) {
        // problem.
        alert('Account miss-configured.');
        gotoHomePage();
    }

    const used_for = isClass ? "class" : "appointment";
    bwData.usableSubscriptions = bwData.subscriptions
        .filter(sub => sub.used_for === used_for);

    if(!bwState.selectedLocation) {
        bwState.selectedLocation = bwData.defaultLocation || null;
    }

    if (!bwState.selectedLocation) {
        useLocationSelector = true;
        // alert('Missing location');
        // gotoHomePage();
    }

    // debugSession();
    // rootContainer.addClass("debug");

    window.bwData = bwData;
    window.bwAuth = bwData.auth;
    // Pagina randată logat + telefon verificat sare pasul de auth, deci controller.js nu construia
    // niciodată bwAuth.state și Finalize pleca cu phone:null. Sămânța asta ține invariantul
    // „isAuth ⇒ state.phone” și, fără telefon cunoscut, readuce pasul de auth (înainte de getVisibleSteps).
    seedAuthState(bwData.auth, bwState);
    // bwData.hasEmployeesFirst = bwData.config.employees_first === 1;

    // Hosted page: ride the signed account token (inlined by booking_v2.blade)
    // as Authorization: Bearer on every call. VerifyCsrfToken's TokenParser
    // bypass then keeps the storeAppointment POST working even when the
    // webview dropped/refused the session cookie — the iOS in-app-browser
    // "Finalize does nothing" bug. Embedded mode already set its own token.
    if (!isWidget && bwData.accountToken) {
        api.setToken(bwData.accountToken);
    }

    // Expose lang globally for i18n (optional)
    if (bwData.translations) {
        window.bwLang = bwData.translations;
    }

    /**
     * ---***###***--- Rendering ---***###***---
     */

    if($('#bookingWidgetIcons').length == 0) {
        body.prepend(svgImages());
    }

    rootContainer.append(renderHeader({currentStep: 1}));
    rootContainer.append('<div class="booking-steps-holder"></div>');
    if(bwData.config.max_services > 1) {
        rootContainer.append(renderBottomBar({maxServices: 3}));
    }

    const $header = rootContainer.find('#booking-header');
    const $stepTitle = $header.find('#booking-step-title');
    const $stepNr = $header.find('.step-nr');
    const $container = rootContainer.find('.booking-steps-holder');
    const $bottomSticky = rootContainer.find('.sticky-bottom');
    const $bottomBar = rootContainer.find('.booking-schedule-bar');
    const $limitWarning = rootContainer.find('.services-limit-section');

    // const steps = getStepOrder(bwData.hasEmployeesFirst);
    // const steps = getEffectiveStepOrder(bwData, bwState);
    // let currentIndex = 0;

    let steps = getVisibleSteps(bwData, bwState);
    let currentIndex = getInitialStepIndex(steps, bwState, bwData);
    let currentModule = null;

    console.log('[BW] Steps:', steps, 'CurrentIndex:', currentIndex);
    console.log('[BW] State:', bwState);
    // console.log('[BW] Selected Employee:', bwState.selectedEmployee);
    // console.log('[BW] Selected Services:', bwState.selectedServices);
    // console.log('[BW] canSelectEmployee:', bwState.canSelectEmployee);
    // console.log('[BW] canSelectService:', bwState.canSelectService);
    // debugger;

    $bottomBar.on('click', 'a', (e) => {
        e.preventDefault();
        $bottomBar.removeClass('active');
        currentModule?.next();
    });

    const dismissLimitWarning = () => {
        Debouncer.cancel(LIMIT_WARNING_ID);
        $limitWarning.removeClass('active');
    };

    $limitWarning.on('mouseover', dismissLimitWarning);
    $limitWarning.on('touchstart', dismissLimitWarning);

    $header.on('click', 'button.step-back-btn', (e) => {
        e.preventDefault();

        const $authController = rootContainer.find('.auth-controller');
        if ($authController.length > 0) {
            const $backBtn = $authController.find('.auth-back-btn[data-step]').first();
            if ($backBtn.length) {
                $backBtn.trigger('click');
                return;
            }
        }

        // No internal auth back — go to previous form step
        prevStep();
    });
    $header.on('click', 'button.step-close-btn', (e) => {
        e.preventDefault();
        // Per-account override: if the account has booking_close_page set,
        // the X navigates there instead of the default home page. Falsy
        // (empty string / null) → keep legacy behavior.
        const closePage = bwData.account && bwData.account.bookingClosePage;
        if (closePage) {
            navigateToTop(closePage);
        } else {
            gotoHomePage();
        }
    });

    // bookingOnly accounts embed only the widget — there's no host home page
    // to close back to, so hide the X. Exception: a configured bookingClosePage
    // gives the X somewhere meaningful to go, so we keep it visible.
    if (bwData.account && bwData.account.bookingOnly && !bwData.account.bookingClosePage) {
        $header.find('button.step-close-btn').hide();
    }

    // Language
    $limitWarning.find('p').text(t('max_services_allowed', {count: bwData.config.max_services}));


    // function doSocialLogin(platform) {
    //     let url;
    //     if(platform === "google") {
    //         url = bwData.apiRoutes.redirectToGoogle;
    //     } else if(platform === "Facebook") {
    //         url = bwData.apiRoutes.facebookRedirect;
    //     } else {
    //         console.error("Unknown platform: ", platform);
    //         return
    //     }
    //
    //     if(bwData.isEmbedded) {
    //         const windowTitle = t('login_with_platform', {platform: t('platform_' + platform)});
    //         const popup = window.open(url, windowTitle, 'width=600,height=600');
    //
    //         if (!popup || popup.closed || typeof popup.closed == 'undefined') {
    //             // Popup was blocked, save state to the server
    //             let state = JSON.stringify(window.state);
    //
    //             // Send the state to the server before redirecting
    //             fetch(bwData.apiRoutes.storeWindowState, {
    //                 method: 'POST',
    //                 headers: {
    //                     'Content-Type': 'application/json',
    //                     'X-CSRF-TOKEN': '{{ csrf_token() }}'
    //                 },
    //                 body: JSON.stringify({ state: state })
    //             }).then(response => {
    //                 // After saving, redirect to Google login as the top navigator
    //                 // ('redirectToGoogle', ['internalUrl' => $model->internal_url, 'page' => 'booking', 'exitPopup' => true])
    //                 top.location.href = url;
    //             }).catch(error => {
    //                 console.error('Error storing state on the server:', error);
    //             });
    //             return;
    //         }
    //
    //         // Listen for postMessage from the popup
    //         window.addEventListener('message', (event) => {
    //             if (!event.origin.endsWith('.ontimeagenda.com') && !event.origin.endsWith('.zenagenda.com')) return;
    //
    //             const { token, user } = event.data;
    //             // Now you can proceed with booking flow
    //
    //             event.source.postMessage('received', event.origin);
    //             console.log(event.data);
    //
    //             // TODO: we are auth and received user and token, continue to next step
    //
    //             app.emit('auth_finish_event', event.data);
    //         });
    //     } else {
    //         top.location.href = url;
    //     }
    // }

    const app = {
        updateHeader({step, titleKey}) {
            $stepNr.text(t('step_of_total', {current: step, total: steps.length}));
            $stepTitle.text(t(titleKey));
        },

        updateBottomBar({selectedCount}) {
            if(bwData.config.max_services > 1 && $bottomBar.length === 0) {
                // We don't have bottom bar.
                return;
            }
            if (selectedCount > 0) {
                $bottomBar.addClass('active');
                rootContainer.addClass('show-bottom');
            } else {
                $bottomBar.removeClass('active');
                rootContainer.removeClass('show-bottom');
            }

            if (selectedCount >= bwData.config.max_services) {
                $limitWarning.addClass('active');

                Debouncer.debounce(LIMIT_WARNING_ID, () => {
                    $limitWarning.removeClass('active');
                }, 2000);
            } else {
                // Immediately remove warning if it was set before
                Debouncer.cancel(LIMIT_WARNING_ID);
                $limitWarning.removeClass('active');
            }
        },
        getInstance() {
            return instanceId;
        },
        emit(event, data) {
            console.log("[Emit]:", event, data);
            console.log("[Emit]:", instanceId, bwState.iid, bwState.selectedEmployee);

            if (event === 'services_footer') {
                app.updateBottomBar({selectedCount: data.size});
            }

            if (event === 'save_state') {
                persistState(instanceId, bwState);
            }

            if (event === 'sub_shop') {
                redirectWithAuth(bwData.apiRoutes.subscriptionShop);
            }
            if (event === 'sub_login') {
                // do auth faster
                const auth_index = steps.indexOf('auth');
                steps.splice(auth_index, 1);
                const sub_index = steps.indexOf('subscription');
                steps.splice(sub_index + 1, 0, 'auth');
                subscriptionLogin = true;
                nextStep();
            }
            if (event === 'sub_next') {
                steps = getVisibleSteps(bwData, bwState);
                bwState.stepContext.employeesFirst = false;
                currentIndex = steps.indexOf('subscription');
                nextStep(); // go to next after subscription
            }
            if (event === 'sub_selected_old') {

                const subscription = data['subscription'];
                const subType = subscription?.type || 'single';

                if(isClass) {
                    // keep logic from class, only mark selected subscription.
                    bwState.selectedSubscription = subscription;
                    // !!! No employees, No services, No calendar
                    nextStep();
                    return;
                }

                if(subType === 'group') {
                    // group is from subscription object - we know we have one
                    bwState.selectedSubscription = subscription;
                    // select all services
                    bwState.selectedServices = subscription.service_ids || [];
                    // remove services as a step
                    const ser_index = steps.indexOf('services');
                    if(ser_index !== -1) {
                        steps.splice(ser_index, 1);
                    }
                    currentIndex = steps.indexOf('employees');
                    bwState.stepContext.employeesFirst = false;
                    renderCurrentStep(); // go to employees
                } else {
                    // single or skip
                    steps = getVisibleSteps(bwData, bwState); // reset steps

                    if(isNonNullObject(subscription)) {
                        bwState.selectedSubscription = subscription;
                        bwState.selectedServices = [];
                        // move services first
                        const ser_index = steps.indexOf('services');
                        const emp_index = steps.indexOf('employees');

                        if (ser_index > emp_index) {
                            // Swap their positions
                            [steps[ser_index], steps[emp_index]] = [steps[emp_index], steps[ser_index]];
                        }
                        bwState.stepContext.employeesFirst = false;

                        currentIndex = steps.indexOf('subscription');
                        nextStep(); // go to next after subscription
                    } else {
                        if(isNonNullObject(bwState.selectedSubscription)) {
                            // we had something selected - reset as normal
                            bwState.selectedServices = [];
                            bwState.selectedEmployee = null;
                        }
                        bwState.selectedSubscription = subscription;
                        steps = getVisibleSteps(bwData, bwState);
                        currentIndex = getInitialStepWithoutSubscriptions(steps, bwState, bwData);
                        renderCurrentStep();
                    }
                }
            }

            if (event === 'sub_selected') {

                const subscription = data['subscription'];

                // Class flow remains untouched
                if (isClass) {
                    bwState.selectedSubscription = subscription;
                    // !!! No employees, No services, No calendar
                    nextStep();
                    return;
                }

                // Apply subscription logic
                setSubscriptionToState(bwState, subscription);

                // Recompute steps AFTER state change
                steps = getVisibleSteps(bwData, bwState);

                if (isNonNullObject(subscription)) {
                    // Move to next step after subscription
                    currentIndex = steps.indexOf('subscription');
                    nextStep();
                } else {
                    // Skip case (-1)
                    currentIndex = getInitialStepWithoutSubscriptions(steps, bwState, bwData);
                    renderCurrentStep();
                }
            }


            if (event === 'location_selected') {
                bwState.selectedLocation = data;
                nextStep();
            }

            if (event === 'services_selected') {
                bwState.selectedServices = data;
                // persistState(instanceId, bwState);
                nextStep();
            }

            if (event === 'employee_selected') {
                // persistState(instanceId, bwState);
                nextStep();
            }

            if (event === 'calendar_selected') {
                // persistState(instanceId, bwState);
                nextStep();
            }

            if (event === 'services_missing') {
                // store() a refuzat rezervarea fara serviciu: inapoi la pasul de servicii. Ora se alege
                // din nou (durata depinde de servicii); locatia si angajatul raman in bwState.
                const si = steps.indexOf('services');
                if (si !== -1) {
                    bwState.startAt = undefined;
                    bwState.endAt = undefined;
                    currentIndex = si;
                    renderCurrentStep();
                }
            }

            if (event === 'slot_taken') {
                // Finalize a gasit ora ocupata intre timp: inapoi la pasul de ore, care se reincarca la
                // render. Serviciile, angajatul si datele clientei raman in bwState.
                const ci = steps.indexOf('calendar');
                if (ci !== -1) {
                    bwState.startAt = undefined;
                    bwState.endAt = undefined;
                    currentIndex = ci;
                    renderCurrentStep();
                }
            }

            if (event === 'reauth_required' && bwData.auth) {
                // Finalize a primit 419 {reason}: sesiunea a murit și serverul n-are dovadă pentru
                // telefon. Cerem codul SMS pe loc, păstrând slotul (bwState e partajat prin referință).
                beginReauth(bwData.auth, bwState, data && data.notes, data && data.reason);
                subscriptionLogin = false; // ramura de abonament din auth_finish_event nu are ce căuta aici
                const ai = steps.indexOf('auth');
                if (ai !== -1) {
                    steps.splice(ai, 1); // sub_login poate să-l fi mutat după 'subscription'
                }
                const ci = steps.indexOf('confirm');
                steps.splice(ci === -1 ? steps.length : ci, 0, 'auth');
                currentIndex = steps.indexOf('auth');
                renderCurrentStep(); // NU getVisibleSteps(): ar rula din nou auto-selecțiile
            }

            if (event === 'auth_finish_event') {
                // Drumul popup-ului trimitea user undefined și ștergea identitatea cunoscută.
                bwData.auth.user = data['user'] || bwData.auth.user; // must have
                bwData.auth.isAuth = true;
                bwData.auth.isChecked = true;
                seedAuthState(bwData.auth, bwState);
                if (bwData.auth.reauth) {
                    // Înapoi direct pe un confirm proaspăt: niciun pas de selecție nu se re-randează
                    // (services.js ar goli slotul), iar confirm.js retrimite singur dacă slotul e același.
                    bwData.auth.reauth = false;
                    const ci = steps.indexOf('confirm');
                    currentIndex = ci === -1 ? steps.length - 1 : ci;
                    renderCurrentStep();
                    return;
                }
                if(subscriptionLogin) {
                    const subscriptions = bwData.auth.user?.all_relevant_subscription_purchases || [];
                    const userHasSubscription = subscriptions && subscriptions.filter(sub => sub.isActive).length > 0;

                    if(userHasSubscription) {
                        // Remove the auth step since we are going back.
                        const auth_index = steps.indexOf('auth');
                        if (auth_index !== -1) {
                            steps.splice(auth_index, 1);
                        }

                        currentIndex = steps.indexOf('subscription');
                        renderCurrentStep();
                    } else {
                        nextStep();
                    }
                } else {
                    // persistState(instanceId, bwState);
                    nextStep();
                }
            }

            // if (event === 'social_login_clicked') {
            //     persistState(instanceId, bwState);
            //     doSocialLogin(data);
            // }

            if (event === 'finish_with_thanks') {
                clearState(instanceId);
                renderStep('thanks');

                // TODO: hide back
                $header.find('button.step-back-btn').hide();
                $stepNr.text(''); // no text, but keep the element to push the close button to the right
            }

            if (event === 'finish_with_redirect') {
                clearState(instanceId);
                navigateToTop(data);
            }
        }
    };

    function gotoHomePage() {
        navigateToTop(bwData.apiRoutes.homePage);
    }
    function navigateToTop(url) {
        if (url &&
            typeof window !== 'undefined' &&
            typeof window.top !== 'undefined' &&
            typeof window.location !== 'undefined' &&
            typeof window.top.location !== 'undefined'
        ) {
            // Ensure we're not in an iframe
            if (window.top !== window.self) {
                window.top.location.href = url;
            } else {
                window.location.href = url;
            }
        }
    }

    function openInNewTab(url, target = '_blank') {
        if (
            url &&
            typeof window !== 'undefined' &&
            typeof window.open === 'function'
        ) {
            window.open(url, target, 'noopener,noreferrer');
        }
    }


    function redirectWithAuth(url) {
        // TODO: is widget????
        if(isWidget) {
            api.post(
                bwData.apiRoutes.authTransfer,
                {
                    data: { redirect_url: url },
                    success: (data) => {
                        if(data && data.hasOwnProperty('transfer_url')) {
                            openInNewTab(data.transfer_url, 'sub-shop');
                        } else {
                            shout("An error occurred with transfer url.");
                            navigateToTop(url);
                        }
                    },
                    error: (err) => {
                        shout("An error occurred while getting transfer url.");
                        navigateToTop(url);
                    }
                }
            );
        } else {
            navigateToTop(url);
        }
    }

    // Set session token after login or widget initialization
    function saveSessionToken(token) {
        try {
            sessionStorage.setItem('bookingWidgetSession', token);
        } catch (e) { /* storage blocked */ }
    }

    // Retrieve it later for API calls
    function getSessionToken() {
        try {
            return sessionStorage.getItem('bookingWidgetSession');
        } catch (e) {
            return null;
        }
    }

    async function updateSessionDebugOutput() {

        let myUrl = window.bwData.apiRoutes.session;
        console.log(myUrl);
        try {
            const res = await fetch(myUrl);
            if (res.ok) {
                const text = await res.text();
                console.log(text);
            } else {
                console.log('Failed to fetch session');
            }
        } catch (err) {
            console.log('Error loading session:', err.message);
        }
    }

    function debugSession() {
        // Also listen for ` key press to update output
        document.addEventListener('keydown', (e) => {
            if (e.key === '`') {
                $('.booking-widget').toggleClass('debug');
                // updateDebugOutput();
            }
            if (e.key === '1') {
                updateSessionDebugOutput();
            }
        });
    }

    function nextStep() {
        currentIndex++;
        if (steps[currentIndex] === 'subscription' && isNonNullObject(bwState.selectedSubscription)) {
            // skip subscription when it's not first step
            if(urlState.selectedSubscription.id == bwState.selectedSubscription.id) {
                // and was already preselected (from url)
                currentIndex++;
            }
        }
        if (steps[currentIndex] === 'auth' && shouldSkipAuthStep(bwData.auth)) {
            currentIndex++;
        }
        renderCurrentStep();
    }

    function prevStep() {
        currentIndex--;
        if (steps[currentIndex] === 'auth' && shouldSkipAuthStep(bwData.auth)) {
            currentIndex--;
        }
        if(currentIndex < 0) {
            gotoHomePage();
            return;
        }
        renderCurrentStep();
    }

    function renderCurrentStep() {
        if (currentIndex >= steps.length) {
            currentIndex = steps.length - 1;
            return;
        }
        if (currentIndex < 0) {
            currentIndex = 0;
            return;
        }
        const stepName = steps[currentIndex];
        renderStep(stepName)
    }
    function renderStep(stepName) {
        const stepModule = stepsMap[stepName]();

        $container.empty();
        app.updateHeader({step: currentIndex + 1, titleKey: stepModule.title_key});

        // Bring the widget back into view on step change. Without this, when
        // the widget is tall and the user scrolled down (e.g. to click a
        // service deep in the list), the next step renders BELOW their
        // current scroll position — header + step title invisible above the
        // viewport. ONLY when the widget top is actually above the viewport:
        // scrollIntoView({block:'start'}) is NOT a no-op for a visible
        // element — it re-pins the top on every call, which yanked the host
        // page on every click on inline embeds (terraoptic bug).
        try {
            const rootEl = rootContainer && rootContainer[0];
            if (rootEl && typeof rootEl.scrollIntoView === 'function'
                && rootEl.getBoundingClientRect().top < 0) {
                rootEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        } catch (_) { /* defensive — never break step rendering on a scroll quirk */ }

        if (typeof stepModule.init === 'function') {
            stepModule.init({
                $,
                state: bwState,
                data: bwData,
                emit: app.emit,
                api
            });
            console.log("renderStep: " + currentIndex, app.getInstance(), instanceId);
        }

        if (typeof stepModule.render === 'function') {
            stepModule.render($container);
        }

        currentModule = stepModule;
        if (stepName === 'services') {
            initCategoryScrollSync({rootContainer, $});
            $bottomSticky.show();
            // Re-sync the bottom bar AFTER the sticky becomes visible. The
            // emit('services_footer') fired inside render() runs while the
            // parent is still display:none (leftover from the previous step
            // hiding it), so the .active toggle there can be visually missed.
            // Recomputing here with the freshly-init'd selection guarantees
            // the "Schedule now" button shows up when the user navigates back
            // to services with a previous selection.
            const selCount = stepModule.selected && typeof stepModule.selected.size === 'number'
                ? stepModule.selected.size
                : 0;
            app.updateBottomBar({selectedCount: selCount});
        } else {
            destroyCategoryScrollSync();
            $bottomBar.removeClass('active');
            $bottomSticky.hide();
            rootContainer.removeClass('show-bottom');
        }
    }

    renderCurrentStep();
}
